import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useRedemptionState } from '@/features/code-redemption';
import { ApiError } from '@/shared/lib/apiClient';
import type {
  IssueParticipantResponse,
  RestoreParticipantResponse,
} from '@/shared/lib/apiTypes';

import { issueParticipant } from '../api/issueParticipant';
import { restoreParticipant } from '../api/restoreParticipant';

/**
 * 유저 코드 발급/복원 흐름을 관장하는 통합 훅.
 *
 * - `issue.mutateAsync()`        : 새 유저 코드를 서버에서 발급받아 즉시 redemption state 에 저장.
 * - `restore.mutateAsync(code)`  : 입력된 코드의 진행 상태를 서버에서 가져와 현재 state 를 통째로 교체.
 * - `clear()`                    : 디버그/초기화용 — 모든 진행 상태를 INITIAL 로 되돌린다.
 *
 * mutation 성공 시 자동으로 redemption state 를 갱신하므로, 호출 측에서는 별도의
 * 상태 동기화를 하지 않아도 된다. 실패는 `ApiError` 로 throw 되어 호출 측에서
 * 토스트나 폼 에러로 표시할 수 있다.
 */
export function useParticipant(): {
  issue: UseMutationResult<IssueParticipantResponse, ApiError, void>;
  restore: UseMutationResult<RestoreParticipantResponse, ApiError, string>;
  clear: () => void;
} {
  const { setUserCode, replaceFromServer, reset } = useRedemptionState();

  const issue = useMutation<IssueParticipantResponse, ApiError, void>({
    mutationFn: () => issueParticipant(),
    onSuccess: (data) => {
      setUserCode(data.code);
    },
    onError: (error) => {
      // 정규화: ApiError 가 아닌 예외도 동일한 형태로 통일해 호출 측 분기를 단순화한다.
      if (!(error instanceof ApiError)) {
        throw new ApiError('알 수 없는 오류가 발생했어요.', 0, undefined, error);
      }
    },
  });

  const restore = useMutation<RestoreParticipantResponse, ApiError, string>({
    mutationFn: (code: string) => restoreParticipant(code),
    onSuccess: (data) => {
      replaceFromServer({
        userCode: data.code,
        codeCount: data.codeCount,
        downloadUrl: data.downloadUrl,
      });
    },
    onError: (error) => {
      if (!(error instanceof ApiError)) {
        throw new ApiError('알 수 없는 오류가 발생했어요.', 0, undefined, error);
      }
    },
  });

  const clear = useCallback(() => {
    reset();
  }, [reset]);

  return { issue, restore, clear };
}
